import {
  DomEvent,
  DomEventHandlerObject,
  DrawingStyle,
  LngLat,
  LngLatBounds,
  YMap,
  YMapLocationRequest
} from "@yandex/ymaps3-types";
import {Feature} from "@yandex/ymaps3-types/packages/clusterer";
import {useApiariesStore} from "entities/apiaries"
import {GetPolygonsParams, PesticideMapPolygonInfo, Point, usePesticideMapStore} from "entities/pesticideMap";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import * as turf from "@turf/turf";
import {COLORS, DEFAULT_LOCATION, regions} from "shared/consts";
import {fromDateToISOStringWithZeroOffset, getRandomUUID} from "shared/helpers";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {IDropdownMenuItem} from "@atomaro/ui-kit/components/DropdownMenu/types";
import {useDebounceCallback} from "shared/lib/debounce/useDebounce";
import {useReactify} from "shared/api";
import {ConfigContext} from "../../../app/App.tsx";

export type ExpandedFeature = Feature & {
  id: string,
  properties?: Record<string, any>
};

type createPointFormValues = {
  coordinates: string,
  radius: string,
}


type onFiltersSubmitParams = {
  dates?: {
    from?: Date | null;
    to?: Date | null;
  };
  status?: string | null;
  regionName?: string | null;
  districtName?: string | null;
  humanHazardClass?: IDropdownMenuItem[];
  beeHazardClass?: IDropdownMenuItem[];
}

export const usePesticideMap = () => {
  const districts = usePesticideMapStore(s => s.districts);
  const apiaries = useApiariesStore(s => s.apiaries);
  const currentApiaryId = useApiariesStore(s => s.currentApiaryId);
  const points = usePesticideMapStore(s => s.points);
  const polygons = usePesticideMapStore(s => s.polygons);
  const isLoading = usePesticideMapStore(s => s.isLoading);
  const getPolygonsFromServer = usePesticideMapStore(s => s.getPolygonsFromServer);
  const addPoint = usePesticideMapStore(s => s.addPoint);
  const editPoint = usePesticideMapStore(s => s.editPoint);
  const deletePoint = usePesticideMapStore(s => s.deletePoint);
  const getPolygonInfo = usePesticideMapStore(s => s.getPolygonInfo);
  const {VITE_YANDEX_TOKEN} = useContext(ConfigContext);

  const mapInstance = useRef<YMap | null>(null);
  const [ location, setLocation ] = useState<YMapLocationRequest>(DEFAULT_LOCATION)
  const [ openApiaryId, setOpenApiaryId ] = useState<string | null>(null);
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState<boolean>(false);
  const [ userCoords, setUserCoords ] = useState<LngLat | null>(null);

  const [ createdPoint, setCreatedPoint ] = useState<Point | null>(null);
  const [ isCreatePointOpen, setIsCreatePointOpen ] = useState<boolean>(false);
  const pointStyle: DrawingStyle = {
    fill: COLORS.SUCCESS.CONTAINER_ACTIVE,
    stroke: [{color: COLORS.SUCCESS.DEFAULT, width: 2}]
  }

  const ddRegex = /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/;

  const createPointForm = useForm<createPointFormValues>({
    defaultValues: {
      coordinates: '',
      radius: '',
    }
  })
  
  const reactify = useReactify(VITE_YANDEX_TOKEN)
  const [ isFiltersOpen, setIsFiltersOpen ] = useState<boolean>(false);
  const [ isNotPesticidesModalOpen, setIsNotPesticidesModalOpen ] = useState<boolean>(false);

  const [ activePolygon, setActivePolygon ] = useState<PesticideMapPolygonInfo | null>(null);
  const [ activePolygonModalPosition, setActivePolygonModalPosition ] = useState<number[] | null>(null);
  const [ isPolygonModalOpen, setIsPolygonModalOpen ] = useState<boolean>(false);

  const getBounds = (coordsArr: {latitude: number, longitude: number }[]) => {
    
    const zoom = mapInstance.current?.zoom || 10;
    const offset = 5 / (zoom * 5);

    const minLat = coordsArr.sort((a, b) => a.latitude - b.latitude)[0].latitude - offset
    const minLong = coordsArr.sort((a, b) => a.longitude - b.longitude)[0].longitude - offset
    const maxLat = coordsArr.sort((a, b) => b.latitude - a.latitude)[0].latitude + offset
    const maxLong = coordsArr.sort((a, b) => b.longitude - a.longitude)[0].longitude + offset

    return [[minLong, minLat], [maxLong, maxLat]] as LngLatBounds
  }

  const apiariesFeatures = useMemo((): ExpandedFeature[] | null => {
    if(!apiaries) {
      return null
    }
    const apiariesMapped: ExpandedFeature[] = apiaries.map(apiary => ({
      type: 'Feature',
      id: apiary.id,
      geometry: {
        type: 'Point',
        coordinates: [Number(apiary.coordinates.longitude), Number(apiary.coordinates.latitude)] as LngLat
      },
      properties: {
        apiary,
      }
    }));
    apiariesMapped.sort((a, b) => {
      if (
        a.geometry.coordinates[0] < b.geometry.coordinates[0] && 
        a.geometry.coordinates[1] > b.geometry.coordinates[1]
      ) {
        return 1
      }

      return -1
    })
    
    return apiariesMapped;
  }, [apiaries])

  const onClusterClick = (features: ExpandedFeature[]) => {
    const coordsArr = features.map(el => ({
      latitude: Number(el.geometry.coordinates[1]),
      longitude: Number(el.geometry.coordinates[0]),
    }))
    if(coordsArr.length > 0) {
      setLocation((prevLocation) => ({
        ...prevLocation,
        bounds: getBounds(coordsArr)
      }))
    } else {
       setLocation((prevLocation) => ({
        ...prevLocation,
      }))
    }
  }

  const isValidLngLat = (coords: unknown): coords is [number, number] => {
    return (
        Array.isArray(coords) &&
        coords.length === 2 &&
        coords.every(
            (v) => typeof v === 'number' && Number.isFinite(v)
        )
    );
  };

  const pointsFeatures = useMemo(() => {
    let pointsClone = structuredClone?.(points) || JSON.parse(JSON.stringify(points));

    if (createdPoint && !createdPoint.id.includes('new')) {
      pointsClone = pointsClone.filter((el) => el?.id !== createdPoint.id);
    }

    const arr = pointsClone
        .filter((point) => isValidLngLat(point.coordinates))
        .map((point) => {
          return {
            point: turf.circle(point.coordinates, point.radius || 1, {
              units: 'kilometers',
              properties: {
                id: `points_point_${point.id}`,
              },
            }),
            feature: {
              type: 'Feature',
              id: `points_feature_${point.id}`,
              geometry: {
                type: 'Point',
                coordinates: point.coordinates as LngLat,
              },
              properties: {
                id: `points_feature_${point.id}`,
              },
            },
          };
        });

    if (createdPoint && isValidLngLat(createdPoint.coordinates)) {
      arr.push({
        point: turf.circle(createdPoint.coordinates, createdPoint.radius, {
          units: 'kilometers',
          properties: {
            id: `points_point_${createdPoint.id}`,
          },
        }),
        feature: {
          type: 'Feature',
          id: `points_feature_${createdPoint.id}`,
          geometry: {
            type: 'Point',
            coordinates: createdPoint.coordinates as LngLat,
          },
          properties: {
            id: `points_feature_${createdPoint.id}`,
          },
        },
      });
    }

    return arr;
  }, [points, createdPoint]);

  const polygonsFeatures = useMemo(() => {
    type TFeature = {
      id: string;
      geometry: {
        type: 'Polygon',
        coordinates: LngLat[][]
      },
      style?: {
        fill: string;
        stroke: {
          width: number;
          color: string;
        }[];
      },
      properties: {
        x: number;
        y: number;
        [key: string]: any;
      }
    }
    const answer: TFeature[] = [];

    for (let index = 0; index < polygons.length; index++) {
      const polygon = polygons[index];
      const findedIndex = answer.findIndex(el => el.properties.x === polygon.x&&el.properties.y === polygon.y);
      if (findedIndex >= 0) {
        answer[findedIndex].id = `polygon_${polygon.id}`;
        answer[findedIndex].geometry.coordinates = polygon.geo.coordinates as LngLat[][];
        continue;
      }

      answer.push({
        id: `polygon_${polygon.id}`,
        geometry: {
          type: 'Polygon',
          coordinates: polygon.geo.coordinates as LngLat[][]
        },
        style: {
          fill: COLORS.ACCENT.CONTAINER_ACTIVE,
          stroke: [{width: 2, color: COLORS.ACCENT.DEFAULT}]
        },
        properties: {
          x: polygon.x,
          y: polygon.y
        }
      })
    }

    return answer
  }, [polygons])

  const createPoint = (coordinates?: LngLat, radius?: number, id?: string) => {
    setIsCreatePointOpen(true);;
    if (coordinates) {
      setCreatedPoint({
        coordinates: coordinates as number[],
        radius: radius || 1,
        id: id || `new_${getRandomUUID()}`
      });
      setLocation((prevLocation) => ({
        ...prevLocation,
        center: coordinates,
        zoom: 12,
        bounds: null,
      }))
  
      createPointForm.setValue('coordinates', coordinates.join(', '));
      createPointForm.setValue('radius', `${radius || 1}`);
    }
  }

  const onCreatePointClose = () => {
    setIsCreatePointOpen(false);
    // const coordsArr = apiaries.map(el => ({
    //   latitude: Number(el.coordinates.latitude),
    //   longitude: Number(el.coordinates.longitude),
    // }))
    setLocation((prevLocation) => ({
      ...prevLocation,
      // bounds: getBounds(coordsArr)
      center: mapInstance.current?.center,
      zoom: (mapInstance.current?.zoom || 10) - 2
    }))

    setCreatedPoint(null);
    createPointForm.reset();
  }

  const onPointClick = (id: string) => {
    const currentPoint = points.find(point => point.id === id);
    if (!currentPoint) return;

    createPoint(currentPoint.coordinates as LngLat, currentPoint.radius, currentPoint.id)
  }

  const openPolygonModal = async (id: string, screenCoordinates: number[]) => {
    const currentPolygon = polygons.find(el => el.id == id);
    if (!currentPolygon) return;

    const polygonInfo = await getPolygonInfo(id);

    if (!polygonInfo) {
      return toast.error('При загрузке данных произошла ошибка, попробуйте позже, пожалуйста');
    }

    setActivePolygon(polygonInfo);
    if (!isPolygonModalOpen) {
      setActivePolygonModalPosition(screenCoordinates);
    }
    setTimeout(() => {
      setIsPolygonModalOpen(true);
    }, 100);
  }

  const closePolygonModal = () => {
    setIsPolygonModalOpen(false);
    setTimeout(() => {
      setActivePolygon(null);
    }, 500);
  }

  const onMapClick = (object: DomEventHandlerObject, event: DomEvent) => {
    if (isCreatePointOpen) {
      const coordinates = event.coordinates;
      return createPoint(coordinates);
    }

    if (object?.type === 'marker') {
      const id = object.entity.properties?.id as string;
      if (id?.includes('points')) {
        onPointClick(id.replace('points_feature_', '').replace('points_point_', ''))
      }
      return;
    }

    if (object?.type === 'feature') {
      const propertiesId = object.entity.properties?.id as string;
      if (propertiesId?.includes('points')) {
        onPointClick(propertiesId.replace('points_feature_', '').replace('points_point_', ''))
        return;
      }

      const id = object.entity.id;
      if (id.includes('polygon')) {
        openPolygonModal(id.replace('polygon_', ''), event.screenCoordinates);
      }
      return;
    }
    
    if (openApiaryId) {
      return setOpenApiaryId(null);
    }

    if (isPolygonModalOpen&&!isCreatePointOpen) {
      return closePolygonModal();
    }
  }

  const onMapUpdate = () => {
    setOpenApiaryId(null);
    closePolygonModal();
    if (!createdPoint&&!isCreatePointOpen) {
      onFiltersSubmitDebouced()
    }
  }

  const onCoordinatesChange = (coordinates: string) => {
    createPointForm.setValue('coordinates', coordinates);
    if (!ddRegex.test(coordinates)) {
      return createPointForm.setError('coordinates', {message: 'Ошибка ввода координат'})
    }

    createPointForm.clearErrors('coordinates');

    const center = coordinates.split(', ').map(el=>Number(el));
    setLocation((prevLocation) => ({
      ...prevLocation,
      center: center as LngLat
    }))
    setCreatedPoint((prevCreatedPoint) => {
      if (prevCreatedPoint) {
        return {
          ...prevCreatedPoint,
          coordinates: center
        }
      }

      return {
        coordinates: center,
        radius: 1,
        id: `new_${getRandomUUID()}`
      }
    });
  }

  const onRadiusChange = (radius: string) => {
    createPointForm.setValue('radius', radius === "0" ? "1" : radius);

    if (!/^[0-9]+(\.[0-9]*)?$/.test(radius)) {
      createPointForm.setError('radius', { message: 'Некорректный радиус' });
      return;
    }

    if (Number(radius) > 15) {
      createPointForm.setError('radius', { message: 'Радиус должен быть не больше 15 км' });
      return;
    }

    createPointForm.clearErrors('radius');

    setCreatedPoint((prevCreatedPoint) => {
      if (prevCreatedPoint) {
        return {
          ...prevCreatedPoint,
          radius: Number(radius),
        };
      }

      const coordStr = createPointForm.getValues('coordinates');

      if (!coordStr || !ddRegex.test(coordStr)) {
        createPointForm.setError('coordinates', {
          message: 'Сначала введите корректные координаты',
        });
        return prevCreatedPoint;
      }

      const center = coordStr.split(',').map((el) => Number(el.trim()));

      return {
        coordinates: center,
        radius: Number(radius),
        id: `new_${getRandomUUID()}`,
      };
    });
  };

  const addPointFn = async (coordinates: number[] | LngLat, radius: number) => {
    if (!currentApiaryId) return;

    const isCreated = await addPoint(coordinates, radius, currentApiaryId);
    if (isCreated) {
      return toast.success('Метка успешно добавлена')
    }

    return toast.error('Произошла ошибка, попробуйте позже пожалуйста')
  }

  const editPointFn = async (point: Point) => {
    if (!currentApiaryId||!point) return;

    const isEdited = await editPoint(point, currentApiaryId)

    if (isEdited) {
      return toast.success('Параметры метки успешно изменены')
    }

    return toast.error('Произошла ошибка, попробуйте позже пожалуйста')
  }

  const onCreatePointSubmit = async (data: createPointFormValues) => {
    if (!currentApiaryId) return;

    if (createdPoint?.id.includes('new')) {
      await addPointFn(data.coordinates.split(', ').map(el=>Number(el)), Number(data.radius));
    } else if (createdPoint) {
      await editPointFn(createdPoint)
    }
    onCreatePointClose();
  }

  const onCloseCreatePointClick = () => {
    if (createdPoint?.id.includes('new')) {
      return onCreatePointClose();
    } else {
      setIsDeleteModalOpen(true);
    }
  }

  const onDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  }

  const onDeleteModalSubmit = async () => {
    if (!currentApiaryId) return;

    setIsDeleteModalOpen(false);
    onCreatePointClose();
    if (!createdPoint) {
      return
    }

    const isDeleted = await deletePoint(createdPoint?.id, currentApiaryId);

    if (isDeleted) {
      return toast.success('Метка удалена')
    }

    return toast.error('Произошла ошибка, попробуйте позже пожалуйста')
  }

  const onFiltersSubmit = useCallback(async (data?: onFiltersSubmitParams) => {
    const replaceParams = Boolean(data);
    const zoom = mapInstance.current?.zoom;

    if (!replaceParams) {
      if (!zoom || zoom < 6) return;
    }

    if (data?.districtName) {
      const finded = districts.find(district => data.districtName === `${district.id}`);
      if (finded) {
        const location: YMapLocationRequest = {
          center: [finded.longitude, finded.latitude],
          zoom: 14
        }

        setLocation(location)
      }
    } else if (data?.regionName) {
      const finded = regions.find(el => `${el.id}` === data.regionName);
      if (finded) {
        const location: YMapLocationRequest = {
          center: [finded.x, finded.y],
          zoom: 10
        }

        setLocation(location)
      }
    }

    setTimeout(() => {
      const bounds = mapInstance.current?.bounds;
      if (!bounds) return;
  
      const payload: GetPolygonsParams = {
        leftTop: {
          latitude: bounds[0][1],
          longitude: bounds[0][0],
        },
        rightDown: {
          latitude: bounds[1][1],
          longitude: bounds[1][0],
        }
      }
      if (data?.dates?.from) {
        payload.startDateUpdateMap = fromDateToISOStringWithZeroOffset(data.dates.from)
      }
      if (data?.dates?.to) {
        payload.endDateUpdateMap = fromDateToISOStringWithZeroOffset(data.dates.to)
      }
      if (data?.beeHazardClass?.length) {
        payload.classesDangerBee = data.beeHazardClass.map(el => `${el.key}`)
      }
      if (data?.humanHazardClass?.length) {
        payload.classesDangerMan = data.humanHazardClass.map(el => `${el.key}`)
      }
      if (data?.status) {
        payload.status = data.status;
      }
      if (data?.regionName) {
        payload.subject = data.regionName;
      }
      if (data?.districtName) {
        payload.district = data.districtName;
      }
  
      getPolygonsFromServer(payload, replaceParams);
    }, 100);
  },[])

  const onFiltersSubmitDebouced = useDebounceCallback(onFiltersSubmit, 1000)

  useEffect(() => {
    const coordsArr = apiaries.map(el => ({
      latitude: Number(el.coordinates.latitude),
      longitude: Number(el.coordinates.longitude),
    }))
    if(coordsArr.length > 0) {
      setLocation((prevLocation) => ({
        ...prevLocation,
        bounds: getBounds(coordsArr)
      }))
    } else {
       setLocation((prevLocation) => ({
        ...prevLocation,
      }))
    }
    onFiltersSubmit({
      dates: {
        from: new Date(),
        to: new Date()
      }
    });
  }, [apiaries])

  useEffect(() => {
    if (!userCoords && reactify) {
      reactify.geolocation.getPosition().then((res: any)=> setUserCoords(res.coords)).catch(console.warn)
    }
  }, [])

  useEffect(() => {
    if (!isLoading) {
      if (!polygons.length) setIsNotPesticidesModalOpen(true)
    }
  }, [isLoading, polygons])

  return {
    location,
    mapInstance,
    apiariesFeatures,
    openApiaryId,
    pointsFeatures,
    pointStyle,
    createdPoint,
    createPointForm,
    isDeleteModalOpen,
    isFiltersOpen,
    isNotPesticidesModalOpen,
    polygonsFeatures,
    activePolygon,
    activePolygonModalPosition,
    isPolygonModalOpen,
    isCreatePointOpen,
    onClusterClick,
    setOpenApiaryId,
    onMapClick,
    onCreatePointClose,
    onCoordinatesChange,
    onRadiusChange,
    onCreatePointSubmit,
    onCloseCreatePointClick,
    onDeleteModalClose,
    onDeleteModalSubmit,
    createPoint,
    setIsFiltersOpen,
    setIsNotPesticidesModalOpen,
    closePolygonModal,
    onMapUpdate,
    onFiltersSubmit
  }
}